import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, DialogRoot, useOverlayTriggerState } from '@gitbook/spine-react';

import type { UseUpgradeSite } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import type { CreateSiteSectionDialogProps } from './CreateSiteSectionDialog';
import {
    CreateSiteSectionGroupDialog,
    type CreateSiteSectionGroupDialogProps,
} from './CreateSiteSectionGroupDialog';
import {
    CreateSiteVariantDialog,
    type CreateSiteVariantDialogProps,
} from './CreateSiteVariantDialog';

interface CreateRootSiteItemButtonProps
    extends Omit<CreateSiteSectionDialogProps, 'space'>,
        Omit<CreateSiteSectionGroupDialogProps, 'siteSections'>,
        Omit<CreateSiteVariantDialogProps, 'siteSection'>,
        Pick<ButtonProps, 'className' | 'label' | 'size' | 'iconOnly' | 'kind'> {
    upgrade: UseUpgradeSite | null;
    siteStructure: api.SiteStructure;
}

export function CreateRootSiteItemButton(props: CreateRootSiteItemButtonProps) {
    const { className, label, size, upgrade, iconOnly, kind, ...dialogProps } = props;
    const createSiteSectionGroupDialogTrigger = useOverlayTriggerState();
    const createSiteSectionDialogTrigger = useOverlayTriggerState();
    const createSiteSectionVariantTrigger = useOverlayTriggerState();
    return (
        <>
            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog {...dialogProps} siteSections={[]} />
            </DialogRoot>
            <DialogRoot {...createSiteSectionDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    target={{
                        type: 'siteSection',
                        siteStructure: props.siteStructure,
                        props: dialogProps,
                    }}
                    organizationId={props.organization.id}
                />
            </DialogRoot>
            <DialogRoot {...createSiteSectionVariantTrigger.rootProps}>
                <CreateSiteVariantDialog {...dialogProps} siteSection={null} />
            </DialogRoot>
            <PaletteButton
                kind={kind}
                leadingIcon={Icon.Plus}
                label={label}
                className={className}
                size={size}
                iconOnly={iconOnly}
                palette={() =>
                    createPalette({
                        groups: [
                            {
                                key: 'add',
                                commands: [
                                    props.siteStructure.type === 'siteSpaces'
                                        ? {
                                              key: 'addVariant',
                                              title: 'Variant',
                                              icon: Icon.Plus,
                                              trigger: createSiteSectionVariantTrigger.open,
                                          }
                                        : null,
                                    {
                                        key: 'addSection',
                                        title: 'Section',
                                        icon: Icon.AddSiteSection,
                                        trigger: upgrade
                                            ? upgrade.onPress
                                            : createSiteSectionDialogTrigger.open,
                                        upgrade: Boolean(upgrade),
                                        badge: getPaidIndicatorBadgeProps({ upgrade: upgrade }),
                                    },
                                    {
                                        key: 'addSectionGroup',
                                        title: 'Group',
                                        icon: Icon.AddSiteGroup,
                                        trigger: upgrade
                                            ? upgrade.onPress
                                            : createSiteSectionGroupDialogTrigger.open,
                                        upgrade: Boolean(upgrade),
                                        badge: getPaidIndicatorBadgeProps({ upgrade: upgrade }),
                                    },
                                ].filter((x) => x !== null),
                            },
                        ],
                    })
                }
            />
        </>
    );
}
